import { toValue, type Ref, isRef } from 'vue';
import md5 from './md5';
import extractTailwindStyles from './extractTailwindStyles';

type CompileCssOptions = {
  memoize?: 'local' | 'session' | false;
  limit?: number;
};

const tailwindWorker = new Worker(
  new URL('../lib/tailwind2.worker.ts', import.meta.url),
  { type: 'module' }
);

const memoizeWithLocalStorage = (
  fn: (a: string) => string | Promise<string>,
  { limit = 0, memoize = 'local' }: CompileCssOptions = {}
) => {
  const storage = memoize === 'local' ? localStorage : sessionStorage;

  return async (a: string) => {
    if (!a) return '';

    // const start = Date.now();
    const key = a;
    const hashedKey = 'zzzmemo-' + md5(key);
    let storedResult: string | null;

    try {
      storedResult = storage.getItem(hashedKey);
    } catch (e) {
      return fn(a);
    }

    // Retrieve the order array from sessionStorage only if limit > 0
    let order =
      limit > 0 ? JSON.parse(storage.getItem('zzzmemo-order') || '[]') : null;

    if (typeof storedResult === 'string') {
      // console.log('memo hit', hashedKey, { str: a }, Date.now() - start);

      if (limit > 0) {
        // use a key, move it to the end
        order = order.filter(k => k !== hashedKey);
        order.push(hashedKey);
        storage.setItem('zzzmemo-order', JSON.stringify(order));
      }

      return storedResult;
    }

    const result = await fn(a);
    // console.log('memo miss', hashedKey, Date.now() - start);

    if (limit > 0 && order.length >= limit) {
      const oldestKey = order.shift();
      storage.removeItem(oldestKey);
    }

    if (limit > 0) {
      order.push(hashedKey);
      storage.setItem('zzzmemo-order', JSON.stringify(order));
    }

    storage.setItem(hashedKey, result);
    return result;
  };
};

const sendToWorker = (worker: Worker, src: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const onMessage = (e: MessageEvent) => {
      const { css, i }: { css: string; i: number } = e.data;
      console.log('css received', i, { css });
      cleanup();
      resolve(css);
    };

    const onError = (e: ErrorEvent) => {
      cleanup();
      reject(e);
    };

    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    // console.log('css sent');
    worker.postMessage(src);
  });

const compileTailwindCss = (yaml = '') => sendToWorker(tailwindWorker, yaml);

const postcssWorker = new Worker(
  new URL('../lib/postcss.worker.ts', import.meta.url),
  { type: 'module' }
);

const compilePostcss = (css = '') =>
  css ? sendToWorker(postcssWorker, css) : '';

/**
 * Mutates a CourseDraft object by compiling its CSS and inserting it into the `compiledCss` property.
 *
 * There are two sources of CSS, each processed with PostCSS in a web worker:  Tailwind styles generated from the YAML, and custom CSS written by the author. If one of the compilation processes times out, the old CSS from that source is kept.
 *
 * @param draft
 */
export default async function insertCompiledCss<
  T extends CourseDraft | Ref<CourseDraft | undefined>
>(
  draft: T,
  { memoize = 'local', limit = 10000 }: CompileCssOptions = {}
): Promise<T> {
  const v = toValue(draft);
  if (!v) return draft;

  const d: CourseDraft = { ...v };
  const timeoutAfter = (ms: number) =>
    new Promise((_, reject) => setTimeout(() => reject('timeout'), ms));

  const [oldPc = '', oldTw = ''] =
    d.compiledCss?.split('\n/* TAILWIND */\n') || [];

  const pc = memoize
    ? memoizeWithLocalStorage(compilePostcss, { limit, memoize })
    : compilePostcss;
  const tw = memoize
    ? memoizeWithLocalStorage(compileTailwindCss, { limit, memoize })
    : compileTailwindCss;

  const yaml = extractTailwindStyles(d.yaml);

  const pcPromise = pc(d.css);
  const twPromise = tw(yaml);
  const timeoutPromise = timeoutAfter(5000);

  const [cssResult, tailwindResult] = await Promise.all([
    Promise.race([pcPromise, timeoutPromise]).catch(e => {
      console.error('timeout');
      return oldPc;
    }),
    Promise.race([twPromise, timeoutPromise]).catch(e => {
      console.error('timeout');
      return oldTw;
    })
  ]);

  d.compiledCss = `${cssResult}\n/* TAILWIND */\n${tailwindResult}`;

  if (isRef<CourseDraft>(draft)) {
    draft.value = d;
    return draft;
  } else {
    return d as T;
  }
}
